import { useEffect, useState, type ReactNode } from 'react';
import { LogOut, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Avatar, Drawer, Kbd, Menu, type MenuItem } from '@/components/ui';
import { useAuth, useSession } from '@/lib/auth/AuthProvider';
import { AUDIENCE_HOME, roleLabel, type Audience } from '@/lib/auth/roles';
import { cn } from '@/lib/cn';
import { useHotkey, useStoredState } from '@/lib/hooks';
import type { NavGroup, NavItem, PaletteCommand } from '@/lib/nav/types';
import { CommandPalette } from './CommandPalette';
import { LogoMark } from './Logo';
import { shellThemes, type ShellTheme } from './theme';

export interface AppShellProps {
  audience: Audience;
  nav: readonly NavGroup[];
  commands?: readonly PaletteCommand[];
  /** Left side of the top bar on desktop (e.g. the property switcher). */
  topbarLeading?: ReactNode;
  /** Right side of the top bar (e.g. the "+ New" menu). */
  topbarTrailing?: ReactNode;
}

/**
 * The frame every signed-in screen lives in: a grouped sidebar on desktop, a bottom tab
 * bar plus "More" sheet on phones, a slim top bar with search, and the page outlet.
 * One component, three themes (owner / resident / internal), driven by a nav config.
 */
export function AppShell({ audience, nav, commands = [], topbarLeading, topbarTrailing }: AppShellProps) {
  const theme = shellThemes[audience];
  const [collapsed, setCollapsed] = useStoredState<boolean>(`movo.sidebar.${audience}`, false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useHotkey('k', (event) => {
    event.preventDefault();
    setPaletteOpen((open) => !open);
  }, { mod: true });

  // New page: start at the top and close the mobile sheet.
  useEffect(() => {
    window.scrollTo(0, 0);
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-dvh lg:flex">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-pop"
      >
        Skip to content
      </a>

      <Sidebar audience={audience} nav={nav} theme={theme} collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar audience={audience} theme={theme} leading={topbarLeading} trailing={topbarTrailing} onOpenPalette={() => setPaletteOpen(true)} />
        <main id="main" tabIndex={-1} className="flex-1 px-4 pb-28 pt-6 outline-none sm:px-6 lg:px-8 lg:pb-12 lg:pt-8">
          <div className="mx-auto w-full max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>

      <MobileTabBar nav={nav} onMore={() => setMoreOpen(true)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} nav={nav} audience={audience} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} nav={nav} commands={commands} />
    </div>
  );
}

/* --------------------------------- Sidebar -------------------------------- */

function Brand({ audience, theme, showText }: { audience: Audience; theme: ShellTheme; showText: boolean }) {
  return (
    <NavLink to={AUDIENCE_HOME[audience]} aria-label="MOVO home" className="flex items-center gap-2.5 rounded-lg">
      <LogoMark fill={theme.logoFill} glyph={theme.logoGlyph} />
      {showText && (
        <>
          <span className={cn('font-display text-lg font-extrabold tracking-tight', theme.brandText)}>MOVO</span>
          <span className={cn('rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide', theme.chip)}>{theme.chipLabel}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarLink({ item, theme, collapsed }: { item: NavItem; theme: ShellTheme; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-0' : 'px-3',
          isActive ? theme.itemActive : theme.item,
        )
      }
    >
      <Icon className="size-[18px] shrink-0" aria-hidden />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

function Sidebar({ audience, nav, theme, collapsed, onToggle }: { audience: Audience; nav: readonly NavGroup[]; theme: ShellTheme; collapsed: boolean; onToggle: () => void }) {
  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-dvh shrink-0 flex-col transition-[width] duration-200 lg:flex',
        collapsed ? 'w-[72px]' : 'w-64',
        theme.sidebar,
      )}
    >
      <div className={cn('flex h-14 shrink-0 items-center border-b', theme.divider, collapsed ? 'justify-center' : 'px-4')}>
        <Brand audience={audience} theme={theme} showText={!collapsed} />
      </div>

      <nav aria-label="Main" className="scroll-thin flex-1 overflow-y-auto px-3 py-4">
        {nav.map((group, index) => (
          <div key={group.id} className={cn(index > 0 && 'mt-5')}>
            {group.label &&
              (collapsed ? (
                <div className={cn('mx-2 mb-2 border-t', theme.divider)} role="presentation" />
              ) : (
                <p className={cn('mb-1.5 px-3 text-2xs font-semibold uppercase tracking-wider', theme.groupLabel)}>{group.label}</p>
              ))}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.id}>
                  <SidebarLink item={item} theme={theme} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn('shrink-0 space-y-1 border-t p-3', theme.divider)}>
        <UserMenu theme={theme} collapsed={collapsed} />
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors', collapsed && 'justify-center px-0', theme.toggle)}
        >
          {collapsed ? <PanelLeftOpen className="size-[18px]" aria-hidden /> : <PanelLeftClose className="size-[18px]" aria-hidden />}
          {!collapsed && 'Collapse'}
        </button>
      </div>
    </aside>
  );
}

/* --------------------------------- User menu ------------------------------- */

function UserMenu({ theme, collapsed }: { theme: ShellTheme; collapsed: boolean }) {
  const session = useSession();
  const { signOut } = useAuth();
  const items: MenuItem[] = [{ label: 'Sign out', icon: LogOut, onSelect: signOut }];
  return (
    <Menu
      label="Account menu"
      side="top"
      align="start"
      triggerClassName={cn('flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors', collapsed && 'justify-center', theme.userCard)}
      header={
        <div className="min-w-0 py-1">
          <p className="truncate text-sm font-semibold text-slate-900">{session.user.fullName}</p>
          <p className="truncate text-xs text-slate-500">{session.user.phoneNumber}</p>
        </div>
      }
      items={items}
      trigger={
        <>
          <Avatar name={session.user.fullName} size="md" />
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{session.user.fullName}</span>
              <span className={cn('block truncate text-xs', theme.userMuted)}>{roleLabel(session.user.role)}</span>
            </span>
          )}
        </>
      }
    />
  );
}

/* ---------------------------------- Top bar -------------------------------- */

function Topbar({ audience, theme, leading, trailing, onOpenPalette }: { audience: Audience; theme: ShellTheme; leading?: ReactNode; trailing?: ReactNode; onOpenPalette: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:px-8">
      <div className="lg:hidden">
        <Brand audience={audience} theme={{ ...theme, brandText: 'text-slate-900', chip: 'bg-amethyst-50 text-amethyst-800', logoFill: '#4B0082', logoGlyph: '#FFFFFF' }} showText={false} />
      </div>
      <div className="min-w-0 flex-1">{leading}</div>

      <button
        type="button"
        onClick={onOpenPalette}
        aria-label="Search pages and actions"
        className="hidden h-9 w-64 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500 transition-colors hover:border-slate-300 hover:bg-white md:flex"
      >
        <Search className="size-4" aria-hidden />
        <span className="flex-1 text-left">Search or jump to…</span>
        <Kbd>⌘K</Kbd>
      </button>
      <button
        type="button"
        onClick={onOpenPalette}
        aria-label="Search pages and actions"
        className="inline-flex size-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 md:hidden"
      >
        <Search className="size-5" aria-hidden />
      </button>

      {trailing}
    </header>
  );
}

/* ------------------------------ Mobile navigation -------------------------- */

function MobileTabBar({ nav, onMore }: { nav: readonly NavGroup[]; onMore: () => void }) {
  const tabs = nav.flatMap((group) => group.items).filter((item) => item.tab);
  return (
    <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden">
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.id}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn('flex min-h-14 flex-col items-center justify-center gap-1 text-2xs font-semibold transition-colors', isActive ? 'text-amethyst-800' : 'text-slate-500')
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('flex h-7 w-12 items-center justify-center rounded-full transition-colors', isActive && 'bg-amethyst-100')}>
                      <Icon className="size-5" aria-hidden />
                    </span>
                    {item.tabLabel ?? item.label}
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
        <li>
          <button type="button" onClick={onMore} className="flex min-h-14 w-full flex-col items-center justify-center gap-1 text-2xs font-semibold text-slate-500">
            <span className="flex h-7 w-12 items-center justify-center">
              <MoreHorizontal className="size-5" aria-hidden />
            </span>
            More
          </button>
        </li>
      </ul>
    </nav>
  );
}

function MoreSheet({ open, onClose, nav, audience }: { open: boolean; onClose: () => void; nav: readonly NavGroup[]; audience: Audience }) {
  const session = useSession();
  const { signOut } = useAuth();
  return (
    <Drawer open={open} onClose={onClose} title="Menu" size="md">
      <div className="space-y-6">
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
          <Avatar name={session.user.fullName} size="lg" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{session.user.fullName}</p>
            <p className="truncate text-sm text-slate-500">{roleLabel(session.user.role)}</p>
          </div>
        </div>
        {nav.map((group) => (
          <section key={group.id} aria-label={group.label ?? 'Home'}>
            {group.label && <h3 className="mb-2 px-1 text-2xs font-semibold uppercase tracking-wider text-slate-400">{group.label}</h3>}
            <ul className="grid grid-cols-2 gap-2">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors',
                          isActive ? 'border-amethyst-200 bg-amethyst-50 text-amethyst-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                        )
                      }
                    >
                      <Icon className="size-4 shrink-0" aria-hidden />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        <button
          type="button"
          onClick={() => {
            onClose();
            signOut();
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
        >
          <LogOut className="size-4" aria-hidden />
          Sign out of {audience === 'internal' ? 'MOVO Internal' : 'MOVO'}
        </button>
      </div>
    </Drawer>
  );
}
